import { useEffect, useState } from "react";
import { Hub } from "./components/Hub";
import { Studio } from "./components/Studio";
import { Settings } from "./components/Settings";
import { UsernameModal } from "./components/UsernameModal";
import { getMe, type MeResult, type PublicUser } from "./lib/api";

// Tiny path router (no dependency): "/" → Hub (public discovery), "/studio" → the creator
// tool. Both are this one SPA, served by the backend's index.html fallback. Backend routes
// (/<username>, /<username>/<slug>, /a/<id>, /api, ...) intercept before the SPA, so only
// "/", "/studio", and other unmatched paths reach here.
function useRoute() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const on = () => setPath(window.location.pathname);
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  function navigate(to: string) {
    window.history.pushState({}, "", to);
    setPath(new URL(to, window.location.origin).pathname);
  }
  return { path, navigate };
}

export function App() {
  const { path, navigate } = useRoute();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [me, setMe] = useState<MeResult | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // A prominent, must-dismiss notice for things the user MUST see — e.g. their auto-top-up
  // trigger (when their card gets charged) changed because they switched to a pricier model.
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void refreshMe();
    const params = new URLSearchParams(window.location.search);
    // Surface an OAuth error if the callback bounced us back with ?auth_error=...
    const err = params.get("auth_error");
    if (err) setAuthError(err);
    // Returning from Stripe Checkout. The webhook is what actually grants the credit, so we
    // poll /api/me a couple times to let it land, then toast. (Grant is idempotent.)
    const topup = params.get("topup");
    if (topup === "success") {
      setToast("Payment received — credits are being added…");
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        await refreshMe();
        const m = await getMe().catch(() => null);
        if ((m && m.creditBalanceUsd > 0) || tries >= 5) {
          clearInterval(poll);
          if (m && m.creditBalanceUsd > 0) setToast(`Credits added — balance $${m.creditBalanceUsd.toFixed(2)}`);
        }
      }, 1500);
    } else if (topup === "cancel") {
      setToast("Checkout canceled — no charge was made.");
    }
    // Clean the query params either way.
    if (err || topup) {
      params.delete("auth_error");
      params.delete("topup");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  async function refreshMe() {
    try {
      setMe(await getMe());
    } catch {
      setMe(null);
    }
  }

  const inStudio = path === "/studio" || path.startsWith("/studio/");
  // The Studio gate reads the explicit billing mode (not "do we have a key OR credits"). A
  // user in 'byo' mode without a key on file needs to set one even if they have a credit
  // balance from before — the toggle is their stated intent. The Hub is just browsing —
  // never nag a hub visitor.
  const needsSetup = inStudio && me !== null && (
    me.billingMode === "byo" ? !me.hasApiKey : !(me.creditBalanceUsd > 0)
  );

  // Force Settings open in the Studio when the account has no key on file yet.
  useEffect(() => {
    if (needsSetup) setSettingsOpen(true);
  }, [needsSetup]);

  function closeSettings() {
    setSettingsOpen(false);
  }

  function onUsernamePicked(user: PublicUser) {
    setMe((prev) => (prev ? { ...prev, user, needsUsername: false } : prev));
  }

  const needsUsername = Boolean(me?.needsUsername);

  return (
    <>
      {inStudio ? (
        <Studio me={me} refreshMe={refreshMe} onOpenSettings={() => setSettingsOpen(true)} navigate={navigate} />
      ) : (
        <Hub me={me} refreshMe={refreshMe} onOpenSettings={() => setSettingsOpen(true)} navigate={navigate} />
      )}
      {authError && (
        <div className="auth-error-banner auth-error-fixed">
          Sign-in failed ({authError}). Please try again.
          <button className="btn-ghost" onClick={() => setAuthError(null)} style={{ marginLeft: 8 }}>✕</button>
        </div>
      )}
      {settingsOpen && (
        <Settings onClose={closeSettings} required={needsSetup} me={me} refreshMe={refreshMe} notify={setNotice} />
      )}
      {needsUsername && me?.user && <UsernameModal suggested={me.suggestedUsername} onDone={onUsernamePicked} />}
      {toast && (
        <div className="auth-error-banner auth-error-fixed" style={{ background: "var(--panel)", color: "var(--fg)" }}>
          {toast}
          <button className="btn-ghost" onClick={() => setToast(null)} style={{ marginLeft: 8 }}>✕</button>
        </div>
      )}
      {notice && (
        <div
          role="alertdialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: "10vh",
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
          }}
          onClick={() => setNotice(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 520,
              margin: "0 16px",
              background: "var(--panel)",
              color: "var(--fg)",
              border: "2px solid var(--accent, #f5a623)",
              borderRadius: 10,
              padding: "20px 22px",
              boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
            }}
          >
            <div data-testid="billing-notice" style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>⚠️ Billing change</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>{notice}</div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="primary" onClick={() => setNotice(null)}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
